import { Dispatch, SetStateAction } from 'react';
import { DraftStatus } from '../@types';
import { StockBrollItem } from '../services/broll-caption/fetchBrollCaptionData';
import {
  AudioModelType,
  CreateActionsListIds,
  CreateVideoSideMenuItemEnum,
  MediaTypeEnum,
  TalkingFaceModelType,
  UploadStepsEnum,
  VideoRatioEnum,
} from './constant';

export interface CreateActionsType {
  id: CreateActionsListIds;
  buttonTitle: string;
  background?: string;
  type: MediaTypeEnum;
  header?: string;
  subheader?: string;
  fileSupported: string[];
  icon: string;
}
export interface ModalContextType {
  isOpen: boolean;
  selectedMediaDetails: CreateActionsType | null;
  handleClick: (type: CreateActionsType) => void;
  handleCloseModal: () => void;
  currentModalStep: UploadStepsEnum;
  handleFileSelect: (files: any) => void;
  fileDetails: Record<string, string> | null;
  uploadURL: string;
  mediaName: string;
  handleMediaNameChange: (value: string) => void;
  audioModel: AudioModelType;
  setAudioModel: Dispatch<SetStateAction<AudioModelType>>;
  handleOnSubmit: (tags?: any) => Promise<void>;
  handleRetry: () => Promise<void>;
  fileUploadPercent: number;
  extractAudioFromAvatar: boolean;
  setExtractAudioFromAvatar: (value: boolean) => void;
  uploadId: string;
  isUploadError: boolean;
  isRetryDisable: boolean;
  handlePreview: () => void;
}

export enum MediaStatus {
  NotStarted = 'NotStarted',
  AvatarFaceDetection = 'AvatarFaceDetection',
  AvatarVideoConvertionToMp4 = 'AvatarVideoConvertionToMp4',
  AvatarCreatingThumbnail = 'AvatarCreatingThumbnail',
  AvatarRemovingBackground = 'AvatarRemovingBackground',
  CompressingVideo = 'CompressingVideo',
  ExtractingAudio = 'ExtractingAudio',
  ExtractingCaption = 'ExtractingCaption',
  GeneratingBroll = 'GeneratingBroll',
  ApplyingAIBroll = 'ApplyingAIBroll',
  InProgress = 'InProgress',
  Success = 'Success',
  Error = 'Error',
  InQueue = 'InQueue',
}

export type MediaStatusType = MediaStatus;

export interface AvatarDataType {
  createdAt: string;
  id: string;
  name: string;
  processedAvatar: string;
  status: MediaStatusType;
  thumbnail: string;
  errorMessage: string;
  isPublic?: boolean;
  noBgThumbnail?: string;
  updatedAt: string;
  uploadId: string;
  userId: string;
  speechContentId: string;
  draftStatus: boolean;
  uploads?: { url?: string; type?: string };
  metaData?: { heyGenGroupId?: string; heyGenMotionId?: string};
}

interface Label {
  key: string;
  value: string;
}

interface UserAudioSample {
  id: string;
  userAudioTrackId: string;
  uploadId: string;
  status: string;
  tags?: Label[]
  createdAt: string;
  uploads: {
    url: string;
  };
}

export interface Audio {
  id: string;
  userId: string;
  name: string;
  thirdPartyIdentifier: string;
  isPublic: boolean;
  deleted: boolean;
  createdAt: string;
  updatedAt: string;
  modelName: string;
  userAudioSample?: UserAudioSample[];
}

export interface InitialVideoData {
  avatarBackground?: string;
  videoName?: string;
  selectedAvatarId?: string;
  videoText?: string;
  videoEffectPrompt?: string;
  selectedAudioId?: string;
  modelName?: TalkingFaceModelType;
  enableMotion?: boolean;
}
export interface CreateVideoContextType {
  canvasRef: React.RefObject<HTMLCanvasElement>;
  selectedAvatar: AvatarDataType | null;
  videoText: string;
  videoEffectPrompt: string;
  selectedAudio: Audio | null;
  videoRatio: VideoRatioEnum;
  videoName: string;
  draftAudioId: string;
  setAvatarBackground: (value: string) => void;
  avatarBackground: string;
  colors: string[];
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  bgColor: string;
  setBgColor: (value: string) => void;
  bgImages: StockBrollItem[];
  setBgImages: (value: StockBrollItem[]) => void;
  selectedImageIndex: number;
  setSelectedImageIndex: (value: number) => void;
  handleSelectAvatar: (avatar: AvatarDataType | null, open?: boolean) => void;
  handleVideoText: (text: string) => void;
  handleVideoEffectPrompt: (text: string) => void;
  handleSelectAudio: (audio: Audio | null) => void;
  currentStep: 'first' | 'second';
  changeStep: () => void;
  changeVideoRatio: (value: VideoRatioEnum) => void;
  selectedSideMenu: CreateVideoSideMenuItemEnum;
  handleMenuSelect: (value: CreateVideoSideMenuItemEnum) => void;
  handleRenderVideo: () => Promise<void>;
  handleVideoName: (name: string) => void;
  handleDraftVideo: ({
    disableNavigation,
  }?: {
    disableNavigation?: boolean;
  }) => Promise<void | string>;
  isRenderInProgress?: boolean;
  draftVideoId?: string;
  initialVideoData: InitialVideoData;
  isOpenPreviewModal: boolean;
  closePreviewModal: () => void;
  handleCloseModel: () => void;
  modelName: TalkingFaceModelType;
  setModelName: Dispatch<SetStateAction<TalkingFaceModelType>>;
  enableMotion: boolean;
  setEnableMotion: Dispatch<SetStateAction<boolean>>;
  isTrain: boolean;
}

export interface CreatVideoSideMenuIconType {
  id: string;
  label: string;
  icon: string;
  handleClick: () => void;
}

export interface VideoDetailsType {
  id: string;
  name: string;
  userId: string;
  avatarId: string;
  userAudioTrackId: string;
  speechContentId: string;
  status: MediaStatusType;
  thumbnail: string | null;
  processedVideo: string | null;
  createdAt: string;
  updatedAt: string;
  avatar?: AvatarDataType;
  draftStatus: DraftStatus;
}

export interface FeatureRestriction {
  planId: string;
  planName: string;
  clonedAudios: number;
  clonedAvatars: number;
}

export interface SubscriptionPlanType {
  id: string;
  name: string;
  description: string;
  interval: 'Yearly' | 'Monthly';
  amount: number;
  currency: string;
  credits: number;
  planExpiration: string;
  isDeprecated: boolean;
  discountPercent: number | null;
  isActive: boolean;
  featureRestriction: FeatureRestriction[];
  createdAt: string;
  updatedAt: string;
}

export interface RequiredCreditsResponse {
  availableCredits: number;
  creditsInUse: number;
  requiredCredits: number;
}

export type BackgroundMusicData = {
  isBackgroundMusic: boolean;
  backgroundMusicVolume: number;
  backgroundMusicFile?: string;
  loopIn?: boolean;
  musicName?: string;
  isValid?: boolean;
};

export enum BrollRenderStatus {
  InDraft = 'InDraft',
  InQueue = 'InQueue',
  InProgress = 'InProgress',
  Error = 'Error',
  Success = 'Success',
}

export enum StockLibrary {
  Pexels = 'pexels',
  StoryBlocks = 'storyBlocks',
  ShutterStock = 'shutterStock',
  Pixabay = 'pixabay',
}

export enum AiAndRandom {
  AI = 'ai',
  Random = 'random',
}

export enum UploadTypes {
  Image = 'Image',
  Video = 'Video',
  Audio = 'Audio',
}

export interface FilteringSortingParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  filters?: {
    renderStatus?: string;
    status?: string;
  };
}

export enum CardTypeEnum {
  Broll = 'broll',
  Avatar = 'avatar',
  LipsyncVideo = 'lipsyncVideo',
  Draft = 'draft',
  AIMedia = 'aIMedia',
  Voiceover = 'voiceover',
  MyBroll = 'MyBroll',
  PaymentMethod = 'PaymentMethod',
  Upscale = 'Upscale',
  Captions = 'Captions',
  AiClipper = 'AiClipper',
}
